import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL } from '../../../lib/businessInfo';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

type Tab = 'financing' | 'broker' | 'sell';

const ALL_TABS: { key: Tab; label: string; icon: string; mobileLabel: string }[] = [
  { key: 'financing', label: 'Get Financing', icon: 'ri-bank-line', mobileLabel: 'Financing' },
  { key: 'broker',    label: 'Find My Car',   icon: 'ri-search-eye-line', mobileLabel: 'Find My Car' },
  { key: 'sell',      label: 'Sell My Car',   icon: 'ri-hand-coin-line', mobileLabel: 'Sell' },
];

const MOBILE_TABS = ALL_TABS.filter(t => t.key !== 'financing');
const DESKTOP_TABS = ALL_TABS;

// ── Financing panel ──────────────────────────────────────────────────────────
function FinancingPanel() {
  const [vehiclePrice, setVehiclePrice] = useState(25000);
  const [downPayment, setDownPayment]   = useState(5000);
  const [loanTerm, setLoanTerm]         = useState(60);

  const monthlyPayment = (() => {
    const principal = vehiclePrice - downPayment;
    if (principal <= 0) return 0;
    const r = 0.0699 / 12;
    const n = loanTerm;
    return Math.round(principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
  })();

  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
      <div>
        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Get Approved <span className="text-[#E8B84B]">in Minutes</span>
        </h3>
        <p className="text-white/75 text-sm sm:text-base mb-6 leading-relaxed">
          Multiple lenders compete for your rate. All credit situations welcome — first-time buyers, rebuilding credit, or just looking for a better deal.
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 list-none p-0">
          {[
            { icon: 'ri-price-tag-3-line', title: '$250 Flat Fee',        sub: 'No hidden charges' },
            { icon: 'ri-user-heart-line', title: 'All Credit Welcome',   sub: 'Even rebuilding' },
            { icon: 'ri-trophy-line', title: 'Best Rate Wins',       sub: 'Lenders compete' },
          ].map(item => (
            <li key={item.title} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
              <div className="w-9 h-9 rounded-lg bg-[#E8B84B]/10 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className={item.icon} style={{ fontSize: '1.25rem', color: '#E8B84B' }}></i>
              </div>
              <div>
                <div className="text-white font-semibold text-xs">{item.title}</div>
                <div className="text-white/60 text-xs">{item.sub}</div>
              </div>
            </li>
          ))}
        </ul>
        <Link
          to="/financing"
          className="inline-flex items-center gap-2 bg-[#E8B84B] text-[#1A2332] px-7 py-3.5 rounded-lg font-bold text-sm hover:bg-[#d4a43d] transition-all shadow-lg whitespace-nowrap cursor-pointer"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          Explore Financing Options
          <span aria-hidden="true">→</span>
        </Link>
      </div>

      {/* Right: Calculator */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-7" role="group" aria-label="Monthly payment estimator">
        <div className="text-center mb-6">
          <div className="text-white/70 text-xs mb-1">Estimated Monthly Payment</div>
          <div className="text-4xl font-bold text-white" aria-live="polite">
            ${monthlyPayment.toLocaleString()}
            <span className="text-xl text-white/70">/mo</span>
          </div>
          <div className="text-white/75 text-xs mt-1">Based on 6.99% APR example rate</div>
        </div>

        <div className="mb-5">
          <div className="flex justify-between mb-1.5">
            <label htmlFor="svc-price" className="text-white text-xs font-medium">Vehicle Price</label>
            <span className="text-[#E8B84B] text-xs font-semibold">${vehiclePrice.toLocaleString()}</span>
          </div>
          <input id="svc-price" type="range" min={5000} max={50000} step={1000} value={vehiclePrice}
            onChange={e => setVehiclePrice(Number(e.target.value))}
            className="w-full h-2.5 rounded-lg appearance-none cursor-pointer accent-[#E8B84B]"
            style={{ background: `linear-gradient(to right,#E8B84B 0%,#E8B84B ${((vehiclePrice-5000)/45000)*100}%,rgba(255,255,255,0.1) ${((vehiclePrice-5000)/45000)*100}%,rgba(255,255,255,0.1) 100%)` }}
          />
        </div>

        <div className="mb-5">
          <div className="flex justify-between mb-1.5">
            <label htmlFor="svc-down" className="text-white text-xs font-medium">Down Payment</label>
            <span className="text-[#E8B84B] text-xs font-semibold">${downPayment.toLocaleString()}</span>
          </div>
          <input id="svc-down" type="range" min={0} max={vehiclePrice} step={500} value={downPayment}
            onChange={e => setDownPayment(Number(e.target.value))}
            className="w-full h-2.5 rounded-lg appearance-none cursor-pointer accent-[#E8B84B]"
            style={{ background: `linear-gradient(to right,#E8B84B 0%,#E8B84B ${(downPayment/vehiclePrice)*100}%,rgba(255,255,255,0.1) ${(downPayment/vehiclePrice)*100}%,rgba(255,255,255,0.1) 100%)` }}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-white mb-2">Loan Term</label>
          <div className="grid grid-cols-4 gap-2">
            {[36, 48, 60, 72].map(m => (
              <button key={m} type="button" onClick={() => setLoanTerm(m)}
                className={`py-2.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${loanTerm === m ? 'bg-[#E8B84B] text-[#1A2332]' : 'bg-white/10 border border-white/20 text-white hover:border-[#E8B84B]'}`}>
                {m} mo
              </button>
            ))}
          </div>
        </div>
        <p className="text-white/70 text-xs text-center mt-4 pt-3 border-t border-white/10">
          Example only. Actual rates vary by credit, vehicle &amp; lender.
        </p>
      </div>
    </div>
  );
}

// ── Broker panel ─────────────────────────────────────────────────────────────
const BROKER_STEPS = [
  { icon: 'ri-search-line', title: 'Tell Us What You Want',  desc: 'Year, make, model, budget — we take it from there.' },
  { icon: 'ri-eye-line', title: 'We Hunt & Inspect',      desc: 'We source from thousands of listings and inspect before you ever see it.' },
  { icon: 'ri-sword-line', title: 'We Negotiate Hard',       desc: 'No emotional attachment. We push for the best price — period.' },
  { icon: 'ri-truck-line', title: 'Keys in Your Driveway',   desc: 'We deliver. You never set foot in a dealership.' },
];

function BrokerPanel() {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Don't Have Time to Hunt?{' '}
          <span className="text-[#E8B84B]">We'll Do It For You.</span>
        </h3>
        <p className="text-white/75 text-sm sm:text-base mb-6 leading-relaxed">
          Our broker service finds, inspects, negotiates, and delivers your perfect vehicle — while you skip the dealership drama entirely.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 border-2 border-teal-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-price-tag-3-line" style={{ fontSize: '1.5rem', color: '#14B8A6' }} aria-hidden="true"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Transparent Flat Fee</h4>
                <p className="text-sm text-gray-600">Simple, upfront pricing with no hidden costs or surprises</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border-2 border-teal-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-scales-line" style={{ fontSize: '1.5rem', color: '#14B8A6' }} aria-hidden="true"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">We Negotiate For You</h4>
                <p className="text-sm text-gray-600">Expert negotiation to get you the best deal possible</p>
              </div>
            </div>
          </div>
        </div>

        <Link
          to="/broker-services/request-a-vehicle"
          className="inline-flex items-center gap-2 bg-[#E8B84B] text-[#1A2332] px-7 py-3.5 rounded-lg font-bold text-sm hover:bg-[#d4a43d] transition-all shadow-lg whitespace-nowrap cursor-pointer mt-6"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
            <i className="ri-search-line"></i>
          </span>
          Request Your Vehicle
          <span aria-hidden="true">→</span>
        </Link>
        <p className="text-white/70 text-xs mt-3">No obligation · Free consultation · Zero dealer pressure</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {BROKER_STEPS.map((step, i) => (
          <div key={step.title} className="relative bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#E8B84B]/40 transition-all">
            <div className="absolute -top-2.5 -left-2.5 w-6 h-6 flex items-center justify-center bg-[#E8B84B] text-[#1A2332] text-xs font-black rounded-full">
              {i + 1}
            </div>
            <div className="w-10 h-10 flex items-center justify-center bg-[#E8B84B]/15 rounded-lg mb-3">
              <i className={step.icon} style={{ fontSize: '1.25rem', color: '#E8B84B' }} aria-hidden="true"></i>
            </div>
            <h4 className="font-bold text-white text-sm mb-1">{step.title}</h4>
            <p className="text-white/75 text-xs leading-relaxed">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sell panel ───────────────────────────────────────────────────────────────
const SELL_POINTS = [
  { icon: 'ri-line-chart-line', title: 'Fair Market Offer',   desc: 'We pay based on real data — not what a dealer thinks they can flip it for.' },
  { icon: 'ri-timer-line', title: 'Offer in 24 Hours',   desc: 'Submit today, get a real cash offer tomorrow. No waiting around.' },
  { icon: 'ri-thumb-up-line', title: 'Zero Obligation',     desc: 'Get an offer. Think it over. Accept or walk away. No pressure, no spam.' },
  { icon: 'ri-file-paper-2-line', title: 'We Handle Paperwork', desc: 'Title transfer, DMV filings — all of it. You just hand over the keys.' },
];

function SellPanel() {
  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
      <div>
        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Looking to Sell?{' '}
          <span className="text-[#E8B84B]">Skip the Lowball.</span>
        </h3>
        <p className="text-white/75 text-sm sm:text-base mb-6 leading-relaxed">
          Most dealers offer you wholesale minus their profit margin. We offer you what the market actually says your car is worth.
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 list-none p-0">
          {SELL_POINTS.map(pt => (
            <li key={pt.title} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="w-9 h-9 flex items-center justify-center bg-[#E8B84B]/15 rounded-lg flex-shrink-0" aria-hidden="true">
                <i className={pt.icon} style={{ fontSize: '1.25rem', color: '#E8B84B' }}></i>
              </div>
              <div>
                <div className="text-white font-bold text-xs mb-0.5">{pt.title}</div>
                <div className="text-white/75 text-xs leading-relaxed">{pt.desc}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/sell-your-car"
            className="inline-flex items-center justify-center gap-2 bg-[#E8B84B] text-[#1A2332] px-7 py-3.5 rounded-lg font-bold text-sm hover:bg-[#d4a43d] transition-all shadow-lg whitespace-nowrap cursor-pointer"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
              <i className="ri-hand-coin-line"></i>
            </span>
            Get Your Offer
          </Link>
          <a
            href={`tel:${PHONE_TEL}`}
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white px-6 py-3.5 rounded-lg font-semibold text-sm hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
              <i className="ri-phone-line"></i>
            </span>
            Call {PHONE_DISPLAY}
          </a>
        </div>
        <p className="text-white/70 text-xs mt-3">
          Or explore our{' '}
          <Link to="/trade-in" className="text-[#E8B84B] hover:underline">trade-in program</Link>{' '}
          if you're buying at the same time.
        </p>
      </div>

      <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ width: '100%', aspectRatio: '4/3' }}>
        <img
          src="https://public.readdy.ai/ai/img_res/5cbc8049552d708d26adba79c619755a.jpg"
          alt="Sell your car to Rockhound Auto — fast, fair, and hassle-free"
          loading="lazy"
          decoding="async"
          width={800}
          height={600}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute bottom-4 left-4 bg-[#E8B84B] text-[#1A2332] px-4 py-2.5 rounded-xl shadow-lg">
          <div className="text-lg font-black" style={{ fontFamily: 'Oswald, sans-serif' }}>No Dealer Lowball</div>
          <div className="text-xs font-semibold opacity-80">We pay what your car is actually worth</div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ServicesTabs() {
  const [activeTab, setActiveTab] = useState<Tab>('broker');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="bg-[#1A2332] py-10 sm:py-20 px-4"
      aria-label="Financing, broker, and sell services"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center mb-7">
          <div
            role="tablist"
            aria-label="Service options"
            className="inline-flex bg-white/5 border border-white/10 rounded-xl p-1 gap-1"
          >
            {/* Mobile tabs */}
            {MOBILE_TABS.map(tab => (
              <button
                key={`mobile-${tab.key}`}
                type="button"
                role="tab"
                id={`svc-tab-mobile-${tab.key}`}
                aria-selected={activeTab === tab.key}
                aria-controls={`svc-panel-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`sm:hidden flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-[#E8B84B] text-[#1A2332] shadow-md'
                    : 'text-white/80 hover:text-white hover:bg-white/5'
                }`}
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                  <i className={tab.icon}></i>
                </span>
                <span>{tab.mobileLabel}</span>
              </button>
            ))}
            {/* Desktop tabs */}
            {DESKTOP_TABS.map(tab => (
              <button
                key={`desktop-${tab.key}`}
                type="button"
                role="tab"
                id={`svc-tab-desktop-${tab.key}`}
                aria-selected={activeTab === tab.key}
                aria-controls={`svc-panel-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`hidden sm:flex items-center gap-2 px-4 sm:px-6 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-[#E8B84B] text-[#1A2332] shadow-md'
                    : 'text-white/80 hover:text-white hover:bg-white/5'
                }`}
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                  <i className={tab.icon}></i>
                </span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div
          id={`svc-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`svc-tab-desktop-${activeTab}`}
          key={activeTab}
          style={{ animation: 'fadeInUp 0.35s ease both' }}
        >
          {activeTab === 'financing' && <FinancingPanel />}
          {activeTab === 'broker'    && <BrokerPanel />}
          {activeTab === 'sell'      && <SellPanel />}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}